/**
 * Service for creating IRIP documents
 */
import { Document, Packer, AlignmentType } from "docx";

import config from "./config.json";

// Document Sections
import createTitlePage from "./createTitlePage";
import createSectionOverallSummary from "./createSectionOverallSummary";
import createSectionGoals from "./createSectionGoals";
import createSectionProgress from "./createSectionProgress";
import createSectionStandards from "./createSectionStandards";
import createSectionMasteryProgress from "./createSectionMasteryProgress";
import createSectionReadingPlan from "./createSectionReadingPlan";
import createSectionAdditionalTeacherSuggestions from "./createSectionAdditionalTeacherSuggestions";
import createSectionPersonalizedInterventionPlan from "./createSectionPersonalizedInterventionPlan";
import { getStudentSkillsMastery } from "@/services/weeklyPlanner/query/getStudentSkillsMastery";

const DOCUMENT_CREATOR = "Amira Learning";
const DOCUMENT_DESCRIPTION = "TODO: Add description";

/**
 * Create and export a complete IRIP document for a student
 * @param {Object} studentData - Student information and reading assessment data
 * @param {Object} weeklyPlan - Curriculum data
 * @param {Object} metrics - Student usage metrics
 * @param {Object} progressData - Student progress report data
 * @returns {Promise<boolean|Blob>} - Promise that resolves with success status or document blob
 */
const createIRIPDocument = async (
  studentData,
  weeklyPlan,
  metrics = {
    year: { stories: 0, minutes: 0 },
    week: { stories: 0, minutes: 0 },
  },
  assignments,
  progressReportId,
  readingRopeReportId
) => {
  try {
    // Provide defaults for missing data
    const safeAssignments = assignments || [];
    const safeWeeklyPlan = weeklyPlan || { curriculum: { tracks: [] }, mastery: {} };
    const safeStudentData = studentData || { firstName: "Student", lastName: "", id: "", grade: "2" };

    const lessonIds = getLessonIds(safeAssignments);
    const units = getUnits(safeWeeklyPlan, lessonIds);
    const focuses = getFocuses(units, lessonIds);
    const masteriesWithEmbeddedFocus = await getMasteriesWithEmbeddedFocus(
      safeWeeklyPlan,
      focuses,
      safeStudentData.id
    );

    const sections = [];

    // We build this from the config for easy development / piecemeal delivery
    if (config.sections.titlePage) {
      sections.push({
        children: [
          ...(await createTitlePage(safeStudentData, safeWeeklyPlan, units, focuses)),
        ],
      });
    }

    // Add the body section if any of the body sections are enabled
    if (
      Object.keys(config.sections.bodyPages).reduce((acc, curr) => {
        return acc || config.sections.bodyPages[curr];
      }, false)
    ) {
      const children = [];

      if (config.sections.bodyPages.overallSummary) {
        children.push(...createSectionOverallSummary(safeStudentData, safeWeeklyPlan, metrics, masteriesWithEmbeddedFocus));
      }

      if (config.sections.bodyPages.goals) {
        children.push(
          ...createSectionGoals(
            safeStudentData,
            safeWeeklyPlan,
            metrics,
            focuses,
            masteriesWithEmbeddedFocus
          )
        );
      }

      if (config.sections.bodyPages.progress) {
        const progressSection = await createSectionProgress(safeStudentData, safeWeeklyPlan);
        children.push(...progressSection);
      }

      if (config.sections.bodyPages.standards) {
        const standardsSection = await createSectionStandards(safeStudentData, safeWeeklyPlan);
        children.push(...standardsSection);
      }

      if (config.sections.bodyPages.masteryProgress) {
        children.push(
          ...(await createSectionMasteryProgress(
            safeStudentData,
            safeWeeklyPlan,
            metrics,
            progressReportId
          ))
        );
      }

      if (config.sections.bodyPages.readingPlan) {
        children.push(
          ...(await createSectionReadingPlan(
            safeStudentData,
            safeWeeklyPlan,
            readingRopeReportId
          ))
        );
      }
      if (config.sections.bodyPages.additionalTeacherSuggestions) {
        children.push(
          ...createSectionAdditionalTeacherSuggestions(safeStudentData, safeWeeklyPlan)
        );
      }

      if (config.sections.bodyPages.personalizedInterventionPlan) {
        children.push(
          ...createSectionPersonalizedInterventionPlan(safeStudentData, safeWeeklyPlan)
        );
      }

      sections.push({
        children: children,
      });
    }

    const doc = new Document({
      creator: DOCUMENT_CREATOR,
      title: `IRIP EL Individualized Reading Plan for ${safeStudentData.firstName} ${safeStudentData.lastName}`,
      description: DOCUMENT_DESCRIPTION,
      sections: sections,
      styles: {
        paragraphStyles: [
          {
            id: "IRIPHeading1",
            name: "IRIP Heading 1",
            basedOn: "Normal",
            next: "IRIPNormal",
            quickFormat: true,
            run: {
              size: 40, // 20pt
              bold: true,
              color: "000000",
            },
            paragraph: {
              spacing: {
                before: 480, // 24pt
                after: 240, // 12pt
                line: 360, // 1.5 lines
              },
              alignment: AlignmentType.LEFT,
            },
          },
          {
            id: "IRIPHeading2",
            name: "IRIP Heading 2",
            basedOn: "Normal",
            next: "IRIPNormal",
            quickFormat: true,
            run: {
              size: 32, // 16pt
              bold: true,
              color: "000000",
            },
            paragraph: {
              spacing: {
                before: 360, // 18pt
                after: 180, // 9pt
                line: 360,
              },
              alignment: AlignmentType.LEFT,
            },
          },
          {
            id: "IRIPHeading3",
            name: "IRIP Heading 3",
            basedOn: "Normal",
            next: "IRIPNormal",
            quickFormat: true,
            run: {
              size: 28, // 14pt
              bold: true,
              color: "000000",
            },
            paragraph: {
              spacing: {
                before: 240, // 12pt
                after: 120, // 6pt
                line: 360,
              },
              alignment: AlignmentType.LEFT,
            },
          },
          {
            id: "IRIPHeading4",
            name: "IRIP Heading 4",
            basedOn: "Normal",
            next: "IRIPNormal",
            quickFormat: true,
            run: {
              size: 24, // 12pt
              bold: true,
              color: "000000",
            },
            paragraph: {
              spacing: {
                before: 120, // 6pt
                after: 60, // 3pt
                line: 360,
              },
              alignment: AlignmentType.LEFT,
            },
          },
          {
            id: "IRIPNormal",
            name: "IRIP Normal",
            basedOn: "Normal",
            next: "IRIPNormal",
            quickFormat: true,
            paragraph: {
              spacing: {
                before: 0,
                after: 0,
                line: 360,
              },
              alignment: AlignmentType.LEFT,
            },
          },
        ],
      },
    });

    // Generate the document as a Blob and return it
    const blob = await Packer.toBlob(doc);
    return blob;
  } catch (error) {
    console.error("Error creating IRIP document:", error);
    return false;
  }
};

const getLessonIds = (assignments) => {
  if (!assignments || !Array.isArray(assignments)) {
    return [];
  }
  return [...new Set(assignments.map((assignment) => assignment?.lessonPlanId).filter(Boolean))];
};

const getUnits = (weeklyPlan, lessonIds) => {
  let units = [];
  
  // Check if weeklyPlan has the expected structure
  if (!weeklyPlan || !weeklyPlan.curriculum || !weeklyPlan.curriculum.tracks) {
    return units;
  }

  try {
    weeklyPlan.curriculum.tracks.forEach((track) => {
      if (track && track.units && Array.isArray(track.units)) {
        track.units.forEach((unit) => {
          if (unit && unit.lessons && Array.isArray(unit.lessons)) {
            unit.lessons.forEach((lesson) => {
              if (lesson && lessonIds.includes(lesson.lessonPlanId)) {
                units.push(unit);
              }
            });
          }
        });
      }
    });
  } catch (error) {
    console.error("Error processing units:", error);
  }
  
  return [...new Set(units)];
};

const getFocuses = (units, lessonIds) => {
  let focuses = [];
  
  if (!units || !Array.isArray(units)) {
    return focuses;
  }

  try {
    units.forEach((unit) => {
      if (unit && unit.lessons && Array.isArray(unit.lessons)) {
        unit.lessons.forEach((lesson) => {
          if (lesson && lessonIds.includes(lesson.lessonPlanId)) {
            // Get focus skills from lesson.skills array
            if (lesson.skills && Array.isArray(lesson.skills)) {
              const focusSkills = lesson.skills.filter(skill => skill.skillType === "FOCUS");
              
              focusSkills.forEach(skill => {
                // Only add if not already in focuses array (check by skillId)
                if (!focuses.some(f => f.skillId === skill.skillId)) {
                  focuses.push({
                    skillId: skill.skillId,
                    displayName: skill.displayName,
                    skillName: skill.skillName,
                    skillType: skill.skillType,
                    lessonId: lesson.lessonPlanId,
                    lessonName: lesson.name,
                    unitName: unit.name,
                    unitId: unit.id,
                    grade: skill.grade,
                    timeToMaster: skill.timeToMaster,
                    preReqSkills: skill.preReqSkills || []
                  });
                }
              });
            }
          }
        });
      }
    });


  } catch (error) {
    console.error("[getFocuses] Error processing focuses:", error);
  }
  
  return focuses;
};

// Helper function to convert pacing to numeric level for comparison
const getMasteryLevel = (pacing) => {
  if (!pacing) return 0;
  switch (pacing) {
    case "AHEAD_OF_GRADE_LEVEL": return 3;
    case "AT_GRADE_LEVEL": return 2;
    case "BELOW_GRADE_LEVEL": return 1;
    default: return 0;
  }
};

const getMasteriesWithEmbeddedFocus = async (weeklyPlan, focuses, studentId) => {
  let masteries = [];
  
  if (!focuses || !Array.isArray(focuses)) {
    return masteries;
  }

  try {
    // Get all unique skill IDs from focuses
    const skillIds = [...new Set(focuses.map(focus => focus.skillId))];

    // Calculate date range for the week
    const currentDate = new Date();
    const weekStartDate = new Date(currentDate);
    weekStartDate.setDate(currentDate.getDate() - currentDate.getDay()); // Go back to Sunday
    weekStartDate.setHours(0, 0, 0, 0);

    // Get mastery data for both start of week and current date
    const [weekStartMastery, currentMastery] = await Promise.all([
      getStudentSkillsMastery({
        studentId: [studentId],
        skillId: skillIds,
        dateFrom: weekStartDate.toISOString().split('T')[0],
        dateTo: weekStartDate.toISOString().split('T')[0]
      }),
      getStudentSkillsMastery({
        studentId: [studentId],
        skillId: skillIds,
        dateFrom: currentDate.toISOString().split('T')[0],
        dateTo: currentDate.toISOString().split('T')[0]
      })
    ]);



    // Process each focus skill and combine with mastery data
    masteries = focuses.map(focus => {
      // Find mastery records for this skill
      const startMasteryRecord = weekStartMastery.studentSkillsMastery?.[0]?.records
        ?.find(record => record.skillId === focus.skillId);
      
      const currentMasteryRecord = currentMastery.studentSkillsMastery?.[0]?.records
        ?.find(record => record.skillId === focus.skillId);

      return {
        focus,
        skillId: focus.skillId,
        displayName: focus.displayName || focus.skillName || focus.skillId,
        weekStartPacing: startMasteryRecord?.pacing || null,
        currentPacing: currentMasteryRecord?.pacing || null,
        hasImproved: startMasteryRecord && currentMasteryRecord && 
          getMasteryLevel(currentMasteryRecord.pacing) > getMasteryLevel(startMasteryRecord.pacing)
      };
    });



  } catch (error) {
    console.error("[getMasteriesWithEmbeddedFocus] Error processing masteries:", error);
  }
  
  return masteries;
};

export default createIRIPDocument;
